import { useGetExamCourseByIdQuery, type ExamCourse } from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo } from 'react';

interface ExamCourseProviderProps extends PropsWithChildren {
  examCourseId: string;
}

type ExaminationCourse = Pick<ExamCourse, 'id' | 'name' | 'code' | 'type' | 'language'>;

interface ExaminationCourseContextData {
  examinationCourse: Pick<ExamCourse, 'id' | 'name' | 'code' | 'type' | 'language'>;
}

const ExaminationCourseContext = createContext<ExaminationCourseContextData | null>(null);

function ExaminationCourseProvider(props: Readonly<ExamCourseProviderProps>) {
  const { children, examCourseId } = props;

  const { data, loading: isLoading } = useGetExamCourseByIdQuery({
    variables: {
      getExamCourseByIdInput: {
        examCourseId,
      },
    },
  });

  const examinationCourse = data?.excGetExamCourseById?.examCourse;

  const contextValue = useMemo(
    () => ({ examinationCourse: examinationCourse as ExaminationCourse }),
    [examinationCourse],
  );

  if (isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  if (!examinationCourse) {
    // TODO: Navigate to error page instead
    return <Navigate to='/exam-courses' replace />;
  }

  return (
    <ExaminationCourseContext.Provider value={contextValue}>
      {children}
    </ExaminationCourseContext.Provider>
  );
}

export { ExaminationCourseContext, ExaminationCourseProvider };
